import "devextreme/dist/css/dx.light.css";
import Vue from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import vuetify from "./plugins/vuetify";

import axios from "axios";
import VueAxios from "vue-axios";

import ElementUI from "element-ui";
import { Button, Input, Form, Alert, FormItem } from "element-ui";
import "element-ui/lib/theme-chalk/index.css";

import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";
import "./assets/css/inter.css";
import "./customstyles.css";
import VueCodeHighlight from "vue-code-highlight";
import * as Sentry from "@sentry/vue";

import { createPinia, PiniaVuePlugin } from "pinia";

import configService from "./services/config";
import launchDarklyService from "./services/launchdarkly";
import {
  getErrorMessage,
  isChunkLoadError,
  tryGuardedChunkReload,
} from "@components/utils/chunkLoadRecovery";

Vue.use(PiniaVuePlugin);
const pinia = createPinia();

import loaderPlugin from "./plugins/loaderPlugin.js";
Vue.use(loaderPlugin, { store });

// Import and use LaunchDarkly plugin
import LaunchDarklyPlugin from "./plugins/launchdarkly.js";
Vue.use(LaunchDarklyPlugin);

// Import and use Notification plugin
import notificationPlugin from "./plugins/notificationPlugin.js";
Vue.use(notificationPlugin);

// Register Web Components
import { registerEditorDatePickerHost } from "./notionEditorDatePickerHost.js";
import { registerEditorLinkPickerHost } from "@components/TiptapEditor/notionEditorLinkPickerHost.js";
registerEditorDatePickerHost();
registerEditorLinkPickerHost();
import "@components/webcomponents-dist/slash-menu-global.css";
import "@components/webcomponents-dist/notion-editor-date-patch.js";
import "@components/webcomponents-dist/notion-editor-link-patch.js";
import "@components/webcomponents-dist/notion-editor-modals-patch.js";
import "@components/webcomponents-dist/notion-editor-slash-patch.js";
import "@components/webcomponents-dist/exiq-webcomponents.es.js";

// Tell Vue to ignore custom elements starting with exiq-
Vue.config.ignoredElements = [/exiq-\w*/];

Vue.config.productionTip = false;

Vue.use(ElementUI);
Vue.component(Button.name, Button);
Vue.component(Input.name, Input);
Vue.component(Form.name, Form);
Vue.component(Alert.name, Alert);
Vue.component(FormItem.name, FormItem);
Vue.use(VueAxios, axios);
Vue.use(VueCodeHighlight);

const options = {
  position: "top-right",
  timeout: 3000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: false,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: true,
  closeButton: "button",
  icon: true,
};
Vue.use(Toast, options);

// ============================================
// Chunk Loading Error Handler
// ============================================
// Handles errors when webpack chunks fail to load (e.g., after deployments)
// This prevents the "SyntaxError: Unexpected token '<'" error when chunks are outdated

/**
 * Sentry duplicate: when a hashed chunk URL returns HTML, the script engine throws
 * SyntaxError: Unexpected token '<' at line 1. tryGuardedChunkReload already captures
 * a structured chunk failure — drop this noisy script-parse event.
 * (Real SyntaxErrors in app source almost never use this message on /js/* bundles.)
 */
const shouldDropSentryChunkHtmlSyntaxError = (event) => {
  const first = event.exception?.values?.[0];
  if (!first || first.type !== "SyntaxError") {
    return false;
  }
  const value = first.value ?? "";
  if (!/Unexpected token ['"]?</i.test(value)) {
    return false;
  }
  const frames = first.stacktrace?.frames ?? [];
  const deployedChunkScript = /\/js\/[^/]+\.m?js(?:[?#].*)?$/i;
  return frames.some((f) => deployedChunkScript.test(f.filename ?? ""));
};

const shouldDropSentryBrowserStorageCloseError = (event) => {
  const first = event.exception?.values?.[0];
  const value = first?.value ?? "";

  return (
    /Connection is closing because of: Force close delete origin/i.test(value) ||
    /AbortError: The transaction was aborted, so the request cannot be fulfilled/i.test(value)
  );
};

// Global error handler for chunk loading failures
window.addEventListener(
  "error",
  (event) => {
    const message = getErrorMessage(event?.message) || getErrorMessage(event?.error);
    const filename = event?.filename || event?.target?.src || "";

    if (
      isChunkLoadError({
        message,
        filename,
        error: event?.error,
      })
    ) {
      const didRecover = tryGuardedChunkReload({
        source: "window.error",
        message,
        filename,
      });

      if (didRecover) {
        event.preventDefault();
      }
    }
  },
  true
);

window.addEventListener(
  "unhandledrejection",
  (event) => {
    const reason = event?.reason;
    const message = getErrorMessage(reason);
    const filename = reason?.fileName || reason?.filename || "";

    if (
      isChunkLoadError({
        message,
        filename,
        error: reason,
      })
    ) {
      const didRecover = tryGuardedChunkReload({
        source: "window.unhandledrejection",
        message,
        filename,
      });

      if (didRecover) {
        event.preventDefault();
      }
    }
  },
  true
);

// ============================================
// Router Chunk Loading Error Handler
// ============================================
// Handle lazy-loaded route component failures
router.onError((error) => {
  const message = getErrorMessage(error);
  const filename = error?.fileName || error?.filename || "";

  if (
    isChunkLoadError({
      message,
      filename,
      error,
    })
  ) {
    tryGuardedChunkReload({
      source: "router.onError",
      message,
      filename,
    });
  }
});

async function initializeApp() {
  try {
    await configService.load();
    Vue.prototype.$config = configService;

    // Check for maintenance mode
    if (configService.get("maintenanceMode")) {
      store.commit("isLoading/setMaintenanceMode", true);
    }

    axios.defaults.baseURL = configService.get("baseURL");

    // Load secure config from localStorage if user is already authenticated
    const token = localStorage.getItem("token");
    const currentAccountID = localStorage.getItem("currentAccountID");
    if (token && currentAccountID) {
      configService.loadSecureConfigFromStorage();
    }

    const DSN_URL = configService.get("dsnUrl");
    const ENVIRONMENT = configService.get("environment");
    const RELEASE = configService.get("sentryRelease");
    if (DSN_URL) {
      Sentry.init({
        Vue,
        dsn: DSN_URL,
        sendDefaultPii: true,
        environment: ENVIRONMENT,
        release: RELEASE,
        integrations: [
          Sentry.browserTracingIntegration({ router }),
          ...(ENVIRONMENT === "production"
            ? [
                Sentry.replayIntegration({
                  maskAllText: false,
                  blockAllMedia: false,
                }),
              ]
            : []),
        ],
        // 40% of page visits (was 100%): 100% used twice the plan's 5M spans in Oct 2026.
        // Agreed with Tauseef; errors and replays are not sampled by this setting.
        tracesSampleRate: 0.4,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: ENVIRONMENT === "production" ? 1.0 : 0,
        // Ignore errors originating from browser extensions — they are false positives
        // that our code cannot control (e.g. DevExtreme WeakMap conflicts triggered
        // by injected extension scripts, refs EXIQTIVE-ACCOUNT-99 & EXIQTIVE-ACCOUNT-3H)
        denyUrls: [
          /chrome-extension:\/\//i,
          /moz-extension:\/\//i,
          /safari-extension:\/\//i,
          /safari-web-extension:\/\//i,
        ],
        ignoreErrors: [
          // Comes exclusively from browser extensions interfering with DevExtreme WeakMap
          "Invalid value used as weak map key",
          // Transient LaunchDarkly client fetch failures; app falls back to default flags
          /^LaunchDarklyFlagFetchError: network error/i,
        ],
        beforeSend(event) {
          const firstException = event.exception?.values?.[0];
          const type = firstException?.type ?? "";
          const value = firstException?.value ?? "";
          if (shouldDropSentryChunkHtmlSyntaxError(event)) {
            return null;
          }
          if (shouldDropSentryBrowserStorageCloseError(event)) {
            return null;
          }
          // Vue Router rejects push/replace to the same URL with NavigationDuplicated;
          // that bypasses router.onError and surfaces as an unhandled rejection in Sentry.
          // Only drop the organization positions *list* path — not …/position/:id, etc.
          if (
            /Avoided redundant navigation to current location: "\/organization\/positions\/?(?:\?[^"]*)?"\./.test(
              value
            )
          ) {
            return null;
          }
          if (type === "LaunchDarklyFlagFetchError" && /network error/i.test(value)) {
            return null;
          }
          // Drop any event whose stack frames are entirely from browser extensions
          const frames = firstException?.stacktrace?.frames ?? [];
          const allExtension =
            frames.length > 0 &&
            frames.every((f) => /^(chrome|moz|safari)-extension:\/\//.test(f.filename ?? ""));
          if (allExtension) return null;
          return event;
        },
      });
    }

    await launchDarklyService.initialize();
    try {
      const token = localStorage.getItem("token");
      const userStr = localStorage.getItem("user");
      const accountId = localStorage.getItem("currentAccountID");
      const accessPermissions = localStorage.getItem("accessPermission");

      if (token && userStr && accountId && accessPermissions) {
        const user = JSON.parse(userStr);
        const permissions = JSON.parse(accessPermissions);
        const account = {
          id: parseInt(accountId),
          name: permissions.account_name || `Account ${accountId}`,
          owner_email: permissions.owner_email || user?.email,
        };

        await launchDarklyService.identifyTenant(user, account);
      }
    } catch (e) {
      console.warn("Could not identify LaunchDarkly context on startup:", e);
    }
  } catch (error) {
    console.error("Application initialization failed:", error);
  }

  new Vue({
    router,
    store,
    vuetify,
    pinia,
    render: (h) => h(App),
  }).$mount("#app");
}

initializeApp();
